import React, { useEffect, useRef } from "react";

/**
 * Custom rings cursor.
 * - Only enabled on fine-pointer devices (mouse users). Falls back to native cursor
 *   on touch/phone/tablet where the coarse pointer detection is truthy.
 * - Two concentric rings + a small dot follow the pointer with soft easing.
 * - Rings expand & tint on interactive elements (a, button, [role=button], input, etc).
 * - Hidden on `mouseleave` (i.e. window blur) and while typing.
 */
export const CustomCursor = () => {
  const rootRef = useRef(null);
  const outerRef = useRef(null);
  const innerRef = useRef(null);
  const dotRef = useRef(null);

  useEffect(() => {
    // Skip entirely on coarse pointer devices (touch)
    const fine =
      window.matchMedia("(hover: hover) and (pointer: fine)").matches;
    if (!fine) return undefined;

    const html = document.documentElement;
    html.classList.add("em-cursor-on");

    const target = { x: window.innerWidth / 2, y: window.innerHeight / 2 };
    const outer = { x: target.x, y: target.y };
    const inner = { x: target.x, y: target.y };
    const dot = { x: target.x, y: target.y };
    let raf = 0;

    const isInteractive = (el) => {
      if (!el) return false;
      return !!el.closest(
        'a, button, [role="button"], input, textarea, select, label, [data-cursor="active"]',
      );
    };

    const onMove = (e) => {
      target.x = e.clientX;
      target.y = e.clientY;
      if (isInteractive(e.target)) html.classList.add("em-cursor-active");
      else html.classList.remove("em-cursor-active");
    };
    const onLeave = () => html.classList.add("em-cursor-hidden");
    const onEnter = () => html.classList.remove("em-cursor-hidden");

    const step = () => {
      // Easing factors: outer ring lags most, dot is snappy.
      dot.x += (target.x - dot.x) * 0.55;
      dot.y += (target.y - dot.y) * 0.55;
      inner.x += (target.x - inner.x) * 0.35;
      inner.y += (target.y - inner.y) * 0.35;
      outer.x += (target.x - outer.x) * 0.18;
      outer.y += (target.y - outer.y) * 0.18;

      if (dotRef.current)
        dotRef.current.style.transform = `translate(${dot.x}px, ${dot.y}px) translate(-50%, -50%)`;
      if (innerRef.current)
        innerRef.current.style.transform = `translate(${inner.x}px, ${inner.y}px) translate(-50%, -50%)`;
      if (outerRef.current)
        outerRef.current.style.transform = `translate(${outer.x}px, ${outer.y}px) translate(-50%, -50%)`;

      raf = requestAnimationFrame(step);
    };

    window.addEventListener("mousemove", onMove, { passive: true });
    document.addEventListener("mouseleave", onLeave);
    document.addEventListener("mouseenter", onEnter);
    raf = requestAnimationFrame(step);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseleave", onLeave);
      document.removeEventListener("mouseenter", onEnter);
      html.classList.remove("em-cursor-on", "em-cursor-active", "em-cursor-hidden");
    };
  }, []);

  return (
    <div className="em-cursor-root" ref={rootRef} aria-hidden="true" data-testid="custom-cursor">
      <div className="em-cursor-ring-outer" ref={outerRef} />
      <div className="em-cursor-ring-inner" ref={innerRef} />
      <div className="em-cursor-dot" ref={dotRef} />
    </div>
  );
};

export default CustomCursor;
